import React from 'react'
import PropTypes from 'prop-types'
import {Icon, createIcon} from '@chakra-ui/react'

// The v2 nav uses Material Symbols glyphs exported from the Figma design system.
// Kept local to the nav rather than added to `../../icons` so they don't collide
// with the existing barrel glyphs of the same name, which other screens depend
// on. All glyphs inherit `color` via `currentColor`.
//
// Product-rail and nav-link icons accept a `filled` prop: NavItem and
// ProductRail set it from the active/selected state. Glyphs that have a distinct
// filled variant (dashboard, move_to_inbox, receipt_long) swap to it; the rest
// have a single shape and simply absorb the prop (so it never reaches the DOM),
// relying on colour to signal the active state.

const makeNavIcon = ({displayName, viewBox, outlined, filled}) => {
  const Component = ({filled: isFilled, ...props}) => (
    <Icon viewBox={viewBox} {...props}>
      {isFilled && filled ? filled : outlined}
    </Icon>
  )
  Component.displayName = displayName
  Component.propTypes = {filled: PropTypes.bool}
  Component.defaultProps = {filled: false}
  return Component
}

export const LocalAtmIcon = makeNavIcon({
  displayName: 'LocalAtmIcon',
  viewBox: '0 0 28 28',
  outlined: (
    <path
      d="M12.8333 19.8333H15.1667V18.6667H16.3333C16.6639 18.6667 16.941 18.5549 17.1646 18.3312C17.3882 18.1076 17.5 17.8306 17.5 17.5V14C17.5 13.6694 17.3882 13.3924 17.1646 13.1687C16.941 12.9451 16.6639 12.8333 16.3333 12.8333H12.8333V11.6667H17.5V9.33333H15.1667V8.16667H12.8333V9.33333H11.6667C11.3361 9.33333 11.059 9.44514 10.8354 9.66875C10.6118 9.89236 10.5 10.1694 10.5 10.5V14C10.5 14.3306 10.6118 14.6076 10.8354 14.8312C11.059 15.0549 11.3361 15.1667 11.6667 15.1667H15.1667V16.3333H10.5V18.6667H12.8333V19.8333ZM4.66667 23.3333C4.025 23.3333 3.47569 23.1049 3.01875 22.6479C2.56181 22.191 2.33333 21.6417 2.33333 21V7C2.33333 6.35833 2.56181 5.80903 3.01875 5.35208C3.47569 4.89514 4.025 4.66667 4.66667 4.66667H23.3333C23.975 4.66667 24.5243 4.89514 24.9813 5.35208C25.4382 5.80903 25.6667 6.35833 25.6667 7V21C25.6667 21.6417 25.4382 22.191 24.9813 22.6479C24.5243 23.1049 23.975 23.3333 23.3333 23.3333H4.66667ZM4.66667 21H23.3333V7H4.66667V21Z"
      fill="currentColor"
    />
  ),
})

export const AccountBalanceWalletIcon = makeNavIcon({
  displayName: 'AccountBalanceWalletIcon',
  viewBox: '0 0 28 28',
  outlined: (
    <path
      d="M5.83333 24.5C5.19167 24.5 4.64236 24.2715 4.18542 23.8146C3.72847 23.3576 3.5 22.8083 3.5 22.1667V5.83333C3.5 5.19167 3.72847 4.64236 4.18542 4.18542C4.64236 3.72847 5.19167 3.5 5.83333 3.5H22.1667C22.8083 3.5 23.3576 3.72847 23.8146 4.18542C24.2715 4.64236 24.5 5.19167 24.5 5.83333V8.75H22.1667V5.83333H5.83333V22.1667H22.1667V19.25H24.5V22.1667C24.5 22.8083 24.2715 23.3576 23.8146 23.8146C23.3576 24.2715 22.8083 24.5 22.1667 24.5H5.83333ZM15.1667 19.8333C14.525 19.8333 13.9757 19.6049 13.5188 19.1479C13.0618 18.691 12.8333 18.1417 12.8333 17.5V10.5C12.8333 9.85833 13.0618 9.30903 13.5188 8.85208C13.9757 8.39514 14.525 8.16667 15.1667 8.16667H23.3333C23.975 8.16667 24.5243 8.39514 24.9812 8.85208C25.4382 9.30903 25.6667 9.85833 25.6667 10.5V17.5C25.6667 18.1417 25.4382 18.691 24.9812 19.1479C24.5243 19.6049 23.975 19.8333 23.3333 19.8333H15.1667ZM23.3333 17.5V10.5H15.1667V17.5H23.3333ZM18.6667 15.75C19.1528 15.75 19.566 15.5799 19.9062 15.2396C20.2465 14.8993 20.4167 14.4861 20.4167 14C20.4167 13.5139 20.2465 13.1007 19.9062 12.7604C19.566 12.4201 19.1528 12.25 18.6667 12.25C18.1806 12.25 17.7674 12.4201 17.4271 12.7604C17.0868 13.1007 16.9167 13.5139 16.9167 14C16.9167 14.4861 17.0868 14.8993 17.4271 15.2396C17.7674 15.5799 18.1806 15.75 18.6667 15.75Z"
      fill="currentColor"
    />
  ),
})

export const DashboardIcon = makeNavIcon({
  displayName: 'DashboardIcon',
  viewBox: '0 0 18 18',
  outlined: (
    <path
      d="M16 2V4H12V2H16ZM6 2V8H2V2H6ZM16 10V16H12V10H16ZM6 14V16H2V14H6ZM18 0H10V6H18V0ZM8 0H0V10H8V0ZM18 8H10V18H18V8ZM8 12H0V18H8V12Z"
      fill="currentColor"
    />
  ),
  filled: (
    <path
      d="M0 10H8V0H0V10ZM0 18H8V12H0V18ZM10 18H18V8H10V18ZM10 0V6H18V0H10Z"
      fill="currentColor"
    />
  ),
})

// `move_to_inbox` — the Payments page glyph in the design (not the card icon).
export const MoveToInboxIcon = makeNavIcon({
  displayName: 'MoveToInboxIcon',
  viewBox: '0 0 18 18',
  outlined: (
    <path
      d="M13 6H10.45V3H7.55V6H5L9 10L13 6ZM16 0H1.99C0.88 0 0 0.9 0 2V16C0 17.1 0.88 18 1.99 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM16 16H2V13H5.56C6.25 14.19 7.53 15 9.01 15C10.49 15 11.76 14.19 12.46 13H16V16ZM16 11H11.01C11.01 12.1 10.11 13 9.01 13C7.91 13 7.01 12.1 7.01 11H2L1.99 2H16V11Z"
      fill="currentColor"
    />
  ),
  filled: (
    <path
      d="M16 0H1.99C0.88 0 0.00999999 0.9 0.00999999 2L0 16C0 17.1 0.88 18 1.99 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM16 12H12C12 13.66 10.65 15 9 15C7.35 15 6 13.66 6 12H1.99V2H16V12ZM13 7H11V4H7V7H5L9 11L13 7Z"
      fill="currentColor"
    />
  ),
})

// The card glyph, used for Points on Payments "Pay bills".
export const PaymentsIcon = makeNavIcon({
  displayName: 'PaymentsIcon',
  viewBox: '0 0 22 16',
  outlined: (
    <path
      d="M18 10V2C18 0.9 17.1 0 16 0H2C0.9 0 0 0.9 0 2V10C0 11.1 0.9 12 2 12H16C17.1 12 18 11.1 18 10ZM16 10H2V2H16V10ZM9 3C7.34 3 6 4.34 6 6C6 7.66 7.34 9 9 9C10.66 9 12 7.66 12 6C12 4.34 10.66 3 9 3ZM22 3V14C22 15.1 21.1 16 20 16H3C3 15 3 15.1 3 14H20V3C21.1 3 21 3 22 3Z"
      fill="currentColor"
    />
  ),
  filled: (
    <path
      d="M18 10V2C18 0.9 17.1 0 16 0H2C0.9 0 0 0.9 0 2V10C0 11.1 0.9 12 2 12H16C17.1 12 18 11.1 18 10ZM9 9C7.34 9 6 7.66 6 6C6 4.34 7.34 3 9 3C10.66 3 12 4.34 12 6C12 7.66 10.66 9 9 9ZM22 3V14C22 15.1 21.1 16 20 16H3V14H20V3H22Z"
      fill="currentColor"
    />
  ),
})

// `redeem` — Convert M-Coin. Single shape; the DS exports filled and outlined
// identically, so the active state is signalled by colour only.
export const RedeemIcon = makeNavIcon({
  displayName: 'RedeemIcon',
  viewBox: '0 0 20 19',
  outlined: (
    <path
      d="M18 4H15.82C15.93 3.69 16 3.35 16 3C16 1.34 14.66 0 13 0C11.95 0 11.04 0.54 10.5 1.35L10 2.02L9.5 1.34C8.96 0.54 8.05 0 7 0C5.34 0 4 1.34 4 3C4 3.35 4.07 3.69 4.18 4H2C0.89 4 0.00999999 4.89 0.00999999 6L0 17C0 18.11 0.89 19 2 19H18C19.11 19 20 18.11 20 17V6C20 4.89 19.11 4 18 4ZM13 2C13.55 2 14 2.45 14 3C14 3.55 13.55 4 13 4C12.45 4 12 3.55 12 3C12 2.45 12.45 2 13 2ZM7 2C7.55 2 8 2.45 8 3C8 3.55 7.55 4 7 4C6.45 4 6 3.55 6 3C6 2.45 6.45 2 7 2ZM18 17H2V15H18V17ZM18 12H2V6H7.08L5 8.83L6.62 10L10 5.4L13.38 10L15 8.83L12.92 6H18V12Z"
      fill="currentColor"
    />
  ),
})

export const ReceiptLongIcon = makeNavIcon({
  displayName: 'ReceiptLongIcon',
  viewBox: '0 0 18 20',
  outlined: [
    <path
      key="body"
      d="M16.5 1.5L15 0L13.5 1.5L12 0L10.5 1.5L9 0L7.5 1.5L6 0L4.5 1.5L3 0V14H0V17C0 18.66 1.34 20 3 20H15C16.66 20 18 18.66 18 17V0L16.5 1.5ZM12 18H3C2.45 18 2 17.55 2 17V16H12V18ZM16 17C16 17.55 15.55 18 15 18C14.45 18 14 17.55 14 17V14H5V3H16V17Z"
      fill="currentColor"
    />,
    <path key="r1l" d="M12 5H6V7H12V5Z" fill="currentColor" />,
    <path key="r1r" d="M15 5H13V7H15V5Z" fill="currentColor" />,
    <path key="r2l" d="M12 8H6V10H12V8Z" fill="currentColor" />,
    <path key="r2r" d="M15 8H13V10H15V8Z" fill="currentColor" />,
  ],
  filled: [
    <path
      key="body"
      d="M16.5 1.5L15 0L13.5 1.5L12 0L10.5 1.5L9 0L7.5 1.5L6 0L4.5 1.5L3 0V14H0V17C0 18.66 1.34 20 3 20H15C16.66 20 18 18.66 18 17V0L16.5 1.5ZM16 17C16 17.55 15.55 18 15 18C14.45 18 14 17.55 14 17V14H5V3H16V17Z"
      fill="currentColor"
    />,
    <path key="r1l" d="M12 5H6V7H12V5Z" fill="currentColor" />,
    <path key="r1r" d="M15 5H13V7H15V5Z" fill="currentColor" />,
    <path key="r2l" d="M12 8H6V10H12V8Z" fill="currentColor" />,
    <path key="r2r" d="M15 8H13V10H15V8Z" fill="currentColor" />,
  ],
})

export const CustomerIcon = makeNavIcon({
  displayName: 'CustomerIcon',
  viewBox: '0 0 16 16',
  outlined: (
    <path
      d="M8 1.9C9.16 1.9 10.1 2.84 10.1 4C10.1 5.16 9.16 6.1 8 6.1C6.84 6.1 5.9 5.16 5.9 4C5.9 2.84 6.84 1.9 8 1.9ZM8 10.9C10.97 10.9 14.1 12.36 14.1 13V14.1H1.9V13C1.9 12.36 5.03 10.9 8 10.9ZM8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0ZM8 9C5.33 9 0 10.34 0 13V16H16V13C16 10.34 10.67 9 8 9Z"
      fill="currentColor"
    />
  ),
})

export const FeaturedSeasonalAndGiftsIcon = createIcon({
  displayName: 'FeaturedSeasonalAndGiftsIcon',
  viewBox: '0 0 24 24',
  path: (
    <path
      d="M4 22.001V11.001H2V5.00098H7.2C7.11667 4.85098 7.0625 4.69264 7.0375 4.52598C7.0125 4.35931 7 4.18431 7 4.00098C7 3.16764 7.29167 2.45931 7.875 1.87598C8.45833 1.29264 9.16667 1.00098 10 1.00098C10.3833 1.00098 10.7417 1.07181 11.075 1.21348C11.4083 1.35514 11.7167 1.55098 12 1.80098C12.2833 1.53431 12.5917 1.33431 12.925 1.20098C13.2583 1.06764 13.6167 1.00098 14 1.00098C14.8333 1.00098 15.5417 1.29264 16.125 1.87598C16.7083 2.45931 17 3.16764 17 4.00098C17 4.18431 16.9833 4.35514 16.95 4.51348C16.9167 4.67181 16.8667 4.83431 16.8 5.00098H22V11.001H20V22.001H4ZM14 3.00098C13.7167 3.00098 13.4792 3.09681 13.2875 3.28848C13.0958 3.48014 13 3.71764 13 4.00098C13 4.28431 13.0958 4.52181 13.2875 4.71348C13.4792 4.90514 13.7167 5.00098 14 5.00098C14.2833 5.00098 14.5208 4.90514 14.7125 4.71348C14.9042 4.52181 15 4.28431 15 4.00098C15 3.71764 14.9042 3.48014 14.7125 3.28848C14.5208 3.09681 14.2833 3.00098 14 3.00098ZM9 4.00098C9 4.28431 9.09583 4.52181 9.2875 4.71348C9.47917 4.90514 9.71667 5.00098 10 5.00098C10.2833 5.00098 10.5208 4.90514 10.7125 4.71348C10.9042 4.52181 11 4.28431 11 4.00098C11 3.71764 10.9042 3.48014 10.7125 3.28848C10.5208 3.09681 10.2833 3.00098 10 3.00098C9.71667 3.00098 9.47917 3.09681 9.2875 3.28848C9.09583 3.48014 9 3.71764 9 4.00098ZM4 7.00098V9.00098H11V7.00098H4ZM11 20.001V11.001H6V20.001H11ZM13 20.001H18V11.001H13V20.001ZM20 9.00098V7.00098H13V9.00098H20Z"
      fill="currentColor"
    />
  ),
})

export const ContactSupportIcon = createIcon({
  displayName: 'ContactSupportIcon',
  viewBox: '0 0 24 24',
  path: (
    <path
      d="M12 2.20508C7.58386 2.20508 4 5.78894 4 10.2051C4 14.6212 7.58386 18.2051 12 18.2051H13V20.9121L13.7666 20.4277C17.5193 18.0566 20 14.1317 20 10.2051C20 5.78894 16.4161 2.20508 12 2.20508ZM12.5 14.7051V15.7051H11.5V14.7051H12.5ZM12 4.20508C13.9339 4.20508 15.5 5.77122 15.5 7.70508C15.5 8.70804 14.9269 9.26132 14.1709 9.92285C13.5468 10.4689 12.7391 11.1245 12.5439 12.2051H11.5283C11.5855 11.7349 11.7233 11.379 11.8965 11.0928C12.131 10.7052 12.45 10.4134 12.8105 10.1279C13.1465 9.862 13.5828 9.55832 13.9004 9.20996C14.2473 8.82934 14.5 8.3544 14.5 7.70508C14.5 6.32894 13.3761 5.20508 12 5.20508C10.795 5.20508 9.78376 6.06684 9.55078 7.20508H8.53613C8.77859 5.50836 10.2359 4.20508 12 4.20508ZM12 19.2207L11.5264 19.1953C6.78102 18.949 3 15.0079 3 10.2051C3 5.24122 7.03614 1.20508 12 1.20508C16.9639 1.20508 21 5.24122 21 10.2051C21 14.9298 17.7019 19.7522 12.7129 22.1543L12 22.498V19.2207Z"
      fill="currentColor"
      stroke="currentColor"
    />
  ),
})

export const NotificationsIcon = createIcon({
  displayName: 'NotificationsIcon',
  viewBox: '0 0 24 24',
  path: (
    <path
      d="M12 21.75C13.1 21.75 14 20.85 14 19.75H10C10 20.85 10.9 21.75 12 21.75ZM18 15.75V10.75C18 7.68 16.37 5.11 13.5 4.43V3.75C13.5 2.92 12.83 2.25 12 2.25C11.17 2.25 10.5 2.92 10.5 3.75V4.43C7.64 5.11 6 7.67 6 10.75V15.75L4 17.75V18.75H20V17.75L18 15.75ZM16 16.75H8V10.75C8 8.27 9.51 6.25 12 6.25C14.49 6.25 16 8.27 16 10.75V16.75Z"
      fill="currentColor"
    />
  ),
})

export const SettingsIcon = createIcon({
  displayName: 'SettingsIcon',
  viewBox: '0 0 24 24',
  path: (
    <path
      d="M19.4318 12.98C19.4718 12.66 19.5018 12.34 19.5018 12C19.5018 11.66 19.4718 11.34 19.4318 11.02L21.5418 9.37C21.7318 9.22 21.7818 8.95 21.6618 8.73L19.6618 5.27C19.5718 5.11 19.4018 5.02 19.2218 5.02C19.1618 5.02 19.1018 5.03 19.0518 5.05L16.5618 6.05C16.0418 5.65 15.4818 5.32 14.8718 5.07L14.4918 2.42C14.4618 2.18 14.2518 2 14.0018 2H10.0018C9.75179 2 9.54179 2.18 9.51179 2.42L9.13179 5.07C8.52179 5.32 7.96179 5.66 7.44179 6.05L4.95179 5.05C4.89179 5.03 4.83179 5.02 4.77179 5.02C4.60179 5.02 4.43179 5.11 4.34179 5.27L2.34179 8.73C2.21179 8.95 2.27179 9.22 2.46179 9.37L4.57179 11.02C4.53179 11.34 4.50179 11.67 4.50179 12C4.50179 12.33 4.53179 12.66 4.57179 12.98L2.46179 14.63C2.27179 14.78 2.22179 15.05 2.34179 15.27L4.34179 18.73C4.43179 18.89 4.60179 18.98 4.78179 18.98C4.84179 18.98 4.90179 18.97 4.95179 18.95L7.44179 17.95C7.96179 18.35 8.52179 18.68 9.13179 18.93L9.51179 21.58C9.54179 21.82 9.75179 22 10.0018 22H14.0018C14.2518 22 14.4618 21.82 14.4918 21.58L14.8718 18.93C15.4818 18.68 16.0418 18.34 16.5618 17.95L19.0518 18.95C19.1118 18.97 19.1718 18.98 19.2318 18.98C19.4018 18.98 19.5718 18.89 19.6618 18.73L21.6618 15.27C21.7818 15.05 21.7318 14.78 21.5418 14.63L19.4318 12.98ZM17.4518 11.27C17.4918 11.58 17.5018 11.79 17.5018 12C17.5018 12.21 17.4818 12.43 17.4518 12.73L17.3118 13.86L18.2018 14.56L19.2818 15.4L18.5818 16.61L17.3118 16.1L16.2718 15.68L15.3718 16.36C14.9418 16.68 14.5318 16.92 14.1218 17.09L13.0618 17.52L12.9018 18.65L12.7018 20H11.3018L10.9518 17.52L9.89179 17.09C9.46179 16.91 9.06179 16.68 8.66179 16.38L7.75179 15.68L6.69179 16.11L5.42179 16.62L4.72179 15.41L5.80179 14.57L6.69179 13.87L6.55179 12.74C6.52179 12.43 6.50179 12.2 6.50179 12C6.50179 11.8 6.52179 11.57 6.55179 11.27L6.69179 10.14L5.80179 9.44L4.72179 8.6L5.42179 7.39L6.69179 7.9L7.73179 8.32L8.63179 7.64C9.06179 7.32 9.47179 7.08 9.88179 6.91L10.9418 6.48L11.1018 5.35L11.3018 4H12.6918L13.0418 6.48L14.1018 6.91C14.5318 7.09 14.9318 7.32 15.3318 7.62L16.2418 8.32L17.3018 7.89L18.5718 7.38L19.2718 8.59L18.2018 9.44L17.3118 10.14L17.4518 11.27ZM12.0018 8C9.79179 8 8.00179 9.79 8.00179 12C8.00179 14.21 9.79179 16 12.0018 16C14.2118 16 16.0018 14.21 16.0018 12C16.0018 9.79 14.2118 8 12.0018 8ZM12.0018 14C10.9018 14 10.0018 13.1 10.0018 12C10.0018 10.9 10.9018 10 12.0018 10C13.1018 10 14.0018 10.9 14.0018 12C14.0018 13.1 13.1018 14 12.0018 14Z"
      fill="currentColor"
    />
  ),
})

export const DoorOpenIcon = createIcon({
  displayName: 'DoorOpenIcon',
  viewBox: '0 0 24 24',
  path: (
    <path
      d="M11 13C11.2833 13 11.5208 12.9042 11.7125 12.7125C11.9042 12.5208 12 12.2833 12 12C12 11.7167 11.9042 11.4792 11.7125 11.2875C11.5208 11.0958 11.2833 11 11 11C10.7167 11 10.4792 11.0958 10.2875 11.2875C10.0958 11.4792 10 11.7167 10 12C10 12.2833 10.0958 12.5208 10.2875 12.7125C10.4792 12.9042 10.7167 13 11 13ZM7 21V19L13 18V6.875C13 6.625 12.925 6.4 12.775 6.2C12.625 6 12.4333 5.88333 12.2 5.85L7 5V3L12.5 3.9C13.2333 4.03333 13.8333 4.375 14.3 4.925C14.7667 5.475 15 6.11667 15 6.85V19.65L7 21ZM3 21V19H5V5C5 4.43333 5.19583 3.95833 5.5875 3.575C5.97917 3.19167 6.45 3 7 3H17C17.5667 3 18.0417 3.19167 18.425 3.575C18.8083 3.95833 19 4.43333 19 5V19H21V21H3ZM7 19H17V5H7V19Z"
      fill="currentColor"
    />
  ),
})

export const LockIcon = createIcon({
  displayName: 'NavLockIcon',
  viewBox: '0 0 16 16',
  path: (
    <path
      d="M12.0003 5.66667H11.3337V4.33333C11.3337 2.49333 9.84033 1 8.00033 1C6.16033 1 4.66699 2.49333 4.66699 4.33333V5.66667H4.00033C3.26699 5.66667 2.66699 6.26667 2.66699 7V13.6667C2.66699 14.4 3.26699 15 4.00033 15H12.0003C12.7337 15 13.3337 14.4 13.3337 13.6667V7C13.3337 6.26667 12.7337 5.66667 12.0003 5.66667ZM6.00033 4.33333C6.00033 3.22667 6.89366 2.33333 8.00033 2.33333C9.10699 2.33333 10.0003 3.22667 10.0003 4.33333V5.66667H6.00033V4.33333ZM12.0003 13.6667H4.00033V7H12.0003V13.6667ZM8.00033 11.6667C8.73366 11.6667 9.33366 11.0667 9.33366 10.3333C9.33366 9.6 8.73366 9 8.00033 9C7.26699 9 6.66699 9.6 6.66699 10.3333C6.66699 11.0667 7.26699 11.6667 8.00033 11.6667Z"
      fill="currentColor"
    />
  ),
})

export const BottomPanelOpenIcon = createIcon({
  displayName: 'BottomPanelOpenIcon',
  viewBox: '0 0 24 24',
  path: (
    <path
      d="M11.5 16V8L7.5 12L11.5 16ZM21 19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19L3 5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19ZM16 19H19V5L16 5V19ZM14 19L14 5H5L5 19H14Z"
      fill="currentColor"
    />
  ),
})

export const UnfoldMoreIcon = createIcon({
  displayName: 'UnfoldMoreIcon',
  viewBox: '0 0 24 24',
  path: (
    <path
      d="M12.0002 5.83L15.1702 9L16.5802 7.59L12.0002 3L7.41016 7.59L8.83016 9L12.0002 5.83ZM12.0002 18.17L8.83016 15L7.42016 16.41L12.0002 21L16.5902 16.41L15.1702 15L12.0002 18.17Z"
      fill="currentColor"
    />
  ),
})
